import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./settings.module.css";
import axios from "axios";
import { API_URL } from "../Utils/api";
import {
  FaUserCircle, FaLock, FaBell, FaGlobe,
  FaCamera, FaEye, FaEyeSlash, FaSignOutAlt,
  FaTrash, FaChevronRight, FaCheck, FaTimes,
  FaShieldAlt, FaUserEdit
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { usePushNotifications } from '../hooks/usePushNotifications';



/* ─── Settings ───────────────────────────────────────────── */
const Settings = ({ setActive }) => {
  const navigate  = useNavigate();
  const token     = localStorage.getItem('token');
  const userId    = localStorage.getItem('userId');
  const fileRef   = useRef(null);

  const [modal,         setModal]         = useState(null);
  const [userData,      setUserData]      = useState(null);
  const [loading,       setLoading]       = useState(false);
  const { isSubscribed, permission, loading: pushLoading, subscribeToPush } = usePushNotifications();

  // Profil
  const [username,      setUsername]      = useState('');
  const [preview,       setPreview]       = useState(null);
  const [imageFile,     setImageFile]     = useState(null);

  // Mot de passe
  const [oldPwd,        setOldPwd]        = useState('');
  const [newPwd,        setNewPwd]        = useState('');
  const [confirmPwd,    setConfirmPwd]    = useState('');
  const [showOld,       setShowOld]       = useState(false);
  const [showNew,       setShowNew]       = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);

  // Erreurs
  const [errors,        setErrors]        = useState({});

  /* ── Fetch user data ── */
  useEffect(() => {
    if (!token || !userId) return;
    axios.get(`${API_URL}/user_data`, {
      headers: { Authorization: `Bearer${token}` }
    }).then(({ data }) => {
      setUserData(data);
      setUsername(data.username || '');
      setPreview(data.userPP || null);
    }).catch(console.error);
  }, [token, userId]);


  const closeModal = useCallback(() => {
    setModal(null);
    setErrors({});
    setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    setImageFile(null);
  }, []);

  /* ── Modifier le profil ── */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    setErrors({});
    if (!username.trim()) {
      setErrors({ username: "Le nom d'utilisateur est requis" });
      return;
    }
    if (username.trim().length < 3) {
      setErrors({ username: "Minimum 3 caractères" });
      return;
    }
    setLoading(true);
    try {
      let base64Image = null;
      if (imageFile) {
        base64Image = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onloadend = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(imageFile);
        });
      }
      await axios.put(`${API_URL}/user/update`, {
        username: username.trim(),
        userPP  : base64Image || undefined,
      }, { headers: { Authorization: `Bearer${token}` } });

      // Mise à jour locale
      localStorage.setItem('username', username.trim());
      setUserData(prev => ({ ...prev, username: username.trim(), userPP: preview }));
      closeModal();
    } catch (e) {
      setErrors({ submit: e.response?.data?.message || 'Erreur lors de la mise à jour' });
    } finally {
      setLoading(false);
    }
  };

  /* ── Changer le mot de passe ── */
  const handleChangePassword = async () => {
    setErrors({});
    if (!oldPwd)           { setErrors({ oldPwd: 'Mot de passe actuel requis' }); return; }
    if (!newPwd)           { setErrors({ newPwd: 'Nouveau mot de passe requis' }); return; }
    if (newPwd.length < 6) { setErrors({ newPwd: 'Minimum 6 caractères' }); return; }
    if (newPwd !== confirmPwd) { setErrors({ confirmPwd: 'Les mots de passe ne correspondent pas' }); return; }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/user/change-password`, {
        oldPassword: oldPwd,
        newPassword: newPwd,
      }, { headers: { Authorization: `Bearer${token}` } });
      closeModal();
    } catch (e) {
      setErrors({ submit: e.response?.data?.message || 'Erreur lors du changement de mot de passe' });
    } finally {
      setLoading(false);
    }
  };

  /* ── Déconnexion ── */
  const handleLogout = () => {
    localStorage.clear();
    navigate('/', { replace: true });
  };

  /* ── Supprimer le compte ── */
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== userData?.username) {
      setErrors({ delete: 'Tape ton nom d\'utilisateur pour confirmer' });
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/user/delete`, {
        headers: { Authorization: `Bearer${token}` }
      });
      localStorage.clear();
      navigate('/', { replace: true });
    } catch (e) {
      setErrors({ submit: e.response?.data?.message || 'Erreur lors de la suppression' });
    } finally {
      setLoading(false);
    }
  };

  /* ── Items ── */
  const sections = [
    {
      title: 'Compte',
      items: [
        {
          id   : 'profile',
          icon : <FaUserEdit />,
          label: 'Modifier le profil',
          sub  : userData ? `@${userData.username}` : '...',
          color: '#2563eb',
        },
        {
          id   : 'password',
          icon : <FaLock />,
          label: 'Changer le mot de passe',
          sub  : '••••••••',
          color: '#7c3aed',
        },
      ]
    },
    {
      title: 'Notifications',
      items: [
        {
          id   : 'push',
          icon : <FaBell />,
          label: 'Notifications Push',
          sub  : permission === 'granted' ? (isSubscribed ? 'Activées' : 'Cliquez pour synchroniser') : 'Activer pour recevoir des alertes',
          color: '#f59e0b',
          action: () => {
            if (permission !== 'granted' || !isSubscribed) {
              subscribeToPush();
            }
          }
        }
      ]
    },
    {
      title: 'Légal',
      items: [
        {
          id   : 'cgu',
          icon : <FaShieldAlt />,
          label: 'Conditions Générales d\'Utilisation',
          sub  : 'Lire les CGU',
          color: '#0891b2',
          action: () => navigate('/cgu'),
        },
        {
          id   : 'privacy',
          icon : <FaGlobe />,
          label: 'Politique de Confidentialité',
          sub  : 'Lire la politique',
          color: '#059669',
          action: () => navigate('/politique-confidentialite'),
        },
      ]
    },
    {
      title: 'Session',
      items: [
        {
          id   : 'logout',
          icon : <FaSignOutAlt />,
          label: 'Se déconnecter',
          sub  : userData?.username ? `Connecté en tant que @${userData.username}` : '',
          color: '#f59e0b',
          action: () => setModal('logout'),
        },
        {
          id   : 'delete',
          icon : <FaTrash />,
          label: 'Supprimer le compte',
          sub  : 'Action irréversible',
          color: '#dc2626',
          danger: true,
          action: () => setModal('delete'),
        },
      ]
    }
  ];

  return (
    <div className={styles.page}>



      {/* ── En-tête profil ── */}
      <div className={styles.profileCard}>
        <div className={styles.avatarWrap}>
          {preview ? (
            <img
              src={preview}
              alt="avatar"
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {userData?.username?.charAt(0).toUpperCase()}
            </div>
          )}
          <button className={styles.avatarEdit} onClick={() => setModal('profile')} aria-label="Modifier la photo">
            <FaCamera />
          </button>
        </div>
        <div className={styles.profileInfo}>
          <p className={styles.profileName}>{userData?.username || '...'}</p>
          <p className={styles.profileEmail}>{userData?.userEmail || ''}</p>
          <p className={styles.profileMeta}>
            {userData?.followers?.length || 0} abonnés · {userData?.following?.length || 0} abonnements
          </p>
        </div>
      </div>

      {/* ── Sections ── */}
      {sections.map(section => (
        <div key={section.title} className={styles.section}>
          <p className={styles.sectionTitle}>{section.title}</p>
          <div className={styles.sectionCard}>
            {section.items.map((item, i) => (
              <div
                key={item.id}
                className={`${styles.item} ${item.danger ? styles.itemDanger : ''} ${i < section.items.length - 1 ? styles.itemBorder : ''}`}
                onClick={item.action || (() => setModal(item.id))}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter') && (item.action ? item.action() : setModal(item.id))}
              >
                <span className={styles.itemIcon} style={{ background: item.color + '18', color: item.color }}>
                  {item.icon}
                </span>
                <div className={styles.itemText}>
                  <span className={styles.itemLabel}>{item.label}</span>
                  {item.sub && <span className={styles.itemSub}>{item.sub}</span>}
                </div>
                <FaChevronRight className={styles.itemArrow} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ══════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════ */}
      {modal && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>

            {/* ── Modal Profil ── */}
            {modal === 'profile' && (
              <>
                <div className={styles.modalHeader}>
                  <h3>Modifier le profil</h3>
                  <button className={styles.modalClose} onClick={closeModal}><FaTimes /></button>
                </div>

                {/* Photo */}
                <div className={styles.avatarModalWrap}>
                  {preview ? (
                    <img
                      src={preview}
                      alt="avatar"
                      className={styles.avatarModal}
                    />
                  ) : (
                    <div className={styles.avatarModalPlaceholder}>
                      {username?.charAt(0).toUpperCase() || userData?.username?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <button className={styles.changePhotoBtn} onClick={() => fileRef.current?.click()}>
                    <FaCamera /> Changer la photo
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display:'none' }} />
                </div>

                {/* Username */}
                <label className={styles.fieldLabel}>Nom d'utilisateur</label>
                <div className={`${styles.inputWrap} ${errors.username ? styles.inputError : ''}`}>
                  <input
                    type="text"
                    value={username}
                    onChange={e => { setUsername(e.target.value); setErrors({}); }}
                    placeholder="nom_utilisateur"
                    className={styles.input}
                    autoFocus
                  />
                </div>
                {errors.username && <p className={styles.errorMsg}>{errors.username}</p>}
                {errors.submit   && <p className={styles.errorMsg}>{errors.submit}</p>}

                <button
                  className={`${styles.btnPrimary} ${loading ? styles.btnLoading : ''}`}
                  onClick={handleSaveProfile}
                  disabled={loading}
                >
                  {loading ? <span className={styles.spinner}/> : 'Enregistrer'}
                </button>
              </>
            )}

            {/* ── Modal Mot de passe ── */}
            {modal === 'password' && (
              <>
                <div className={styles.modalHeader}>
                  <h3>Changer le mot de passe</h3>
                  <button className={styles.modalClose} onClick={closeModal}><FaTimes /></button>
                </div>

                {[
                  { label: 'Mot de passe actuel',    val: oldPwd,     set: setOldPwd,     show: showOld,     setShow: setShowOld,     key: 'oldPwd'     },
                  { label: 'Nouveau mot de passe',   val: newPwd,     set: setNewPwd,     show: showNew,     setShow: setShowNew,     key: 'newPwd'     },
                  { label: 'Confirmer le mot de passe', val: confirmPwd, set: setConfirmPwd, show: showConfirm, setShow: setShowConfirm, key: 'confirmPwd' },
                ].map(field => (
                  <div key={field.key} className={styles.fieldWrap}>
                    <label className={styles.fieldLabel}>{field.label}</label>
                    <div className={`${styles.inputWrap} ${styles.inputWrapIcon} ${errors[field.key] ? styles.inputError : ''}`}>
                      <input
                        type={field.show ? 'text' : 'password'}
                        value={field.val}
                        onChange={e => { field.set(e.target.value); setErrors({}); }}
                        className={styles.input}
                        placeholder="••••••••"
                      />
                      <button type="button" className={styles.eyeBtn} onClick={() => field.setShow(v => !v)}>
                        {field.show ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {errors[field.key] && <p className={styles.errorMsg}>{errors[field.key]}</p>}
                  </div>
                ))}

                {errors.submit && <p className={styles.errorMsg}>{errors.submit}</p>}
                <button
                  className={`${styles.btnPrimary} ${loading ? styles.btnLoading : ''}`}
                  onClick={handleChangePassword}
                  disabled={loading}
                >
                  {loading ? <span className={styles.spinner}/> : 'Modifier le mot de passe'}
                </button>
              </>
            )}

            {/* ── Modal Déconnexion ── */}
            {modal === 'logout' && (
              <>
                <div className={styles.modalHeader}>
                  <h3>Se déconnecter</h3>
                  <button className={styles.modalClose} onClick={closeModal}><FaTimes /></button>
                </div>
                <p className={styles.modalText}>
                  Es-tu sûr(e) de vouloir te déconnecter de ton compte <strong>@{userData?.username}</strong> ?
                </p>
                <div className={styles.btnRow}>
                  <button className={styles.btnSecondary} onClick={closeModal}>Annuler</button>
                  <button className={styles.btnWarning}   onClick={handleLogout}>Se déconnecter</button>
                </div>
              </>
            )}

            {/* ── Modal Supprimer compte ── */}
            {modal === 'delete' && (
              <>
                <div className={styles.modalHeader}>
                  <h3>Supprimer le compte</h3>
                  <button className={styles.modalClose} onClick={closeModal}><FaTimes /></button>
                </div>
                <div className={styles.dangerBox}>
                  <FaTrash />
                  <p>Cette action est <strong>irréversible</strong>. Toutes tes publications, messages et données seront supprimés définitivement.</p>
                </div>
                <label className={styles.fieldLabel}>
                  Tape <strong>@{userData?.username}</strong> pour confirmer
                </label>
                <div className={`${styles.inputWrap} ${errors.delete ? styles.inputError : ''}`}>
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={e => { setDeleteConfirm(e.target.value); setErrors({}); }}
                    placeholder={userData?.username}
                    className={styles.input}
                  />
                </div>
                {errors.delete && <p className={styles.errorMsg}>{errors.delete}</p>}
                {errors.submit && <p className={styles.errorMsg}>{errors.submit}</p>}
                <div className={styles.btnRow}>
                  <button className={styles.btnSecondary} onClick={closeModal}>Annuler</button>
                  <button
                    className={`${styles.btnDanger} ${loading ? styles.btnLoading : ''}`}
                    onClick={handleDeleteAccount}
                    disabled={loading || deleteConfirm !== userData?.username}
                  >
                    {loading ? <span className={styles.spinner}/> : 'Supprimer définitivement'}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;